import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserService from '../services/userService';
import LoadindForButton from '../components/LoadingForButton';
import StepProgress from '../components/StepProgress';
import WeddingForm from '../components/WeddingForm';
import { IoEyeOffOutline, IoEyeOutline, IoMailOpenOutline } from "react-icons/io5";
import { FaEnvelope, FaLock, FaArrowRight, FaChevronUp, FaChevronDown, FaUser } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import Footer from '../components/Footer';
import AuthSideVisual from '../components/AuthSideVisual';
import ServerStatusNotice from '../components/ServerStatusNotice';

const getInitialTimer = () => {
    const start = localStorage.getItem('resend_timer_start');
    if (!start) return 0;
    const elapsed = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
    const left = 60 - elapsed;
    return left > 0 ? left : 0;
};

const Register = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        language: '',
        nickname: '',
    });

    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [step, setStep] = useState(1);
    const [resendTimer, setResendTimer] = useState(getInitialTimer);
    const [invalidCredentials, setInvalidCredentials] = useState(false);

    const [passwordConditions, setPasswordConditions] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
    });

    const [openDropdown, setOpenDropdown] = useState(null);

    const { t, i18n } = useTranslation();
    const isLoggedIn = useSelector((state) => state.userStore.isLoggedIn);
    const serverStatus = useSelector((state) => state.serverStore.status);

    useEffect(() => {
        let isRegistered = sessionStorage.getItem('registeredEmail');
        if (isRegistered?.length > 0) setStep(2);
    }, [isLoggedIn]);

    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = setInterval(() => {
            const start = localStorage.getItem('resend_timer_start');
            if (!start) return;
            const elapsed = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
            const left = 60 - elapsed;
            if (left <= 0) {
                setResendTimer(0);
                localStorage.removeItem('resend_timer_start');
                clearInterval(interval);
            } else {
                setResendTimer(left);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [resendTimer > 0]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors({ ...errors, [name]: '' });
        setFormData({ ...formData, [name]: value });
        if (name === 'password') checkPasswordConditions(value);
        setInvalidCredentials(false);
    };

    const validate = () => {
        const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = t('emailRequired');
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = t('invalidEmailFormat');
        } else if (formData.email.endsWith('.')) {
            newErrors.email = t('invalidEmailFormat');
        }

        if (!formData.password) {
            newErrors.password = t('passwordRequired');
        } else if (formData.password.length > 50) {
            newErrors.password = t('maxLengthError', { count: 50 });
        } else if (formData.password.length < 8) {
            newErrors.password = t('passwordTooShort');
        } else if (!/[A-Z]/.test(formData.password)) {
            newErrors.password = t('passwordUppercase');
        } else if (!/[a-z]/.test(formData.password)) {
            newErrors.password = t('passwordLowercase');
        } else if (!/[0-9]/.test(formData.password)) {
            newErrors.password = t('passwordNumber');
        }

        return newErrors;
    };

    const checkPasswordConditions = (value) => {
        setPasswordConditions({
            length: value.length >= 8,
            uppercase: /[A-Z]/.test(value),
            lowercase: /[a-z]/.test(value),
            number: /[0-9]/.test(value),
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            setIsSubmitted(true);
        } else {
            setErrors({});
            UserService.registerUser({ ...formData, language: i18n.language })
                .then(() => {
                    setLoading(false);
                    setStep(2);
                    sessionStorage.setItem('registeredEmail', formData.email, { path: '/' });
                })
                .catch((err) => {
                    if (err.response.data.message.includes('email already exists')) {
                        setErrors({ email: err.response.data.message });
                    } else {
                        setInvalidCredentials(true);
                    }
                    setLoading(false);
                });
        }
    };

    const handleSubmitVerification = (e) => {
        e.preventDefault();
        setLoading(true);
        if (!verificationCode) {
            setErrors({ verificationCode: t('verificationCodeRequired') });
            setLoading(false);
            return;
        }
        if (verificationCode.length !== 5) {
            setErrors({ verificationCode: t('invalidVerificationCode') });
            setLoading(false);
            return;
        }
        UserService.activateAccount({ verificationCode, email: formData.email || sessionStorage.getItem('registeredEmail') })
            .then(() => {
                sessionStorage.removeItem('registeredEmail');
                localStorage.removeItem('resend_timer_start');
                setLoading(false);
                setStep(3);
            })
            .catch(() => {
                // console.log('error');
                setLoading(false);
                setInvalidCredentials(true);
            });
    };

    const handleBackToStep1 = () => {
        setStep(1);
        setVerificationCode('');
        setErrors({});
        setInvalidCredentials(false);
        sessionStorage.removeItem('registeredEmail');
        localStorage.removeItem('resend_timer_start');
    };

    const handleResendCode = () => {
        UserService.resendCode({ email: formData.email || sessionStorage.getItem('registeredEmail') })
            .then(() => {
                localStorage.setItem('resend_timer_start', Date.now().toString());
                setResendTimer(60);
            })
            .catch(() => { });
    };

    return (
        <>
            <Helmet>
                <title>{t('meta.register.title')}</title>
                <meta name="description" content={t('meta.register.description')} />
            </Helmet>
            <div className="min-h-screen bg-gradient-to-br from-cyan-200 via-white to-pink-300 flex flex-col">
                <main className='flex-1'>
                    <div className='flex justify-between items-center px-4 py-4 lg:px-10 relative'>
                        <Link to="/" className="flex items-center gap-2 shrink-0 no-underline">
                            <img src="/logo2.png" className="w-8 h-8 md:w-9 md:h-9" alt="Weddinfy" />
                            <span className="font-display text-xl md:text-[22px] font-semibold text-stone-900">Weddinfy</span>
                        </Link>
                        <div className="flex items-center space-x-4">
                            {/* Language Dropdown */}
                            {/* <div className="relative" tabIndex={0} onBlur={() => setOpenDropdown(null)}>
                                <div className="bg-white border border-gray-500 rounded-xl px-2 cursor-pointer flex items-center" onClick={() => setOpenDropdown(prev => prev === "language" ? null : "language")}>
                                    <span className="text-gray-800 uppercase">
                                        {i18n.language === 'hr' && '🌐'}
                                        {i18n.language === 'en' && '🇬🇧'}
                                        {i18n.language === 'de' && '🇩🇪'}
                                    </span>
                                    {openDropdown === "language" ? (
                                        <FaChevronUp className="ml-2 text-gray-600" />
                                    ) : (
                                        <FaChevronDown className="ml-2 text-gray-600" />
                                    )}
                                </div>

                                {openDropdown === "language" && (
                                    <div className="absolute right-0 mt-1 z-50 w-full min-w-[130px] bg-white border border-gray-500 rounded-xl shadow-lg">
                                        {[
                                            { code: 'hr', label: 'Jezik', flag: '🌐' },
                                            { code: 'en', label: 'English', flag: '🇬🇧' },
                                            { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
                                        ].map((lang) => (
                                            <button key={lang.code} type="button"
                                                onMouseDown={() => {
                                                    i18n.changeLanguage(lang.code);
                                                    localStorage.setItem('lang', lang.code);
                                                    setOpenDropdown(null);
                                                }}
                                                className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-xl flex items-center gap-2">
                                                <span className="text-xl">{lang.flag}</span>
                                                <span>{lang.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div> */}
                        </div>
                    </div>
                    <div className="lg:px-10 flex flex-col lg:flex-row h-full ">
                        <div className='flex justify-center'>
                            <img src="/logo2.png" className="w-12 h-12 lg:hidden" alt="Weddinfy" />
                        </div>
                        <div className="w-full lg:w-1/2 max-w-screen-md mx-auto">
                            <StepProgress currentStep={step} />
                            {step === 1 && (
                                <>
                                    <form className="p-4 w-full" onSubmit={handleSubmit}>
                                        <div className="relative mt-4">
                                            <label>{t('email')}</label>
                                            <FaEnvelope className="inputWeddingIcon" />
                                            <input
                                                type="email"
                                                name="email"
                                                maxLength={50}
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`inputWedding ${errors.email ? '!border-red-500' : 'inputFieldNormal'}`}
                                            />
                                        </div>
                                        {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}

                                        <div className="absolute mt-4 left-[-4000px]">
                                            <label>{t('nickname')}</label>
                                            <FaUser className="inputWeddingIcon" />
                                            <input
                                                type="text"
                                                name="nickname"
                                                maxLength={50}
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                tabIndex={-1}
                                                className={`inputWedding ${errors.nickname ? '!border-red-500' : 'inputFieldNormal'}`}
                                            />
                                        </div>

                                        <div className="relative mt-4">
                                            <label>{t('password')}</label>
                                            <FaLock className="inputWeddingIcon" />
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                maxLength={50}
                                                value={formData.password}
                                                onChange={handleChange}
                                                className={`inputWedding ${errors.password ? '!border-red-500' : 'inputFieldNormal'}`}
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute bottom-3 right-4">
                                                {showPassword ? <IoEyeOutline size={20} /> : <IoEyeOffOutline size={20} />}
                                            </button>
                                        </div>
                                        {!formData.password && <p className="text-red-500 text-sm">{errors.password}</p>}

                                        {isSubmitted && formData.password && (
                                            <div className="text-sm bg-red-100 p-3 rounded-md mt-4 mb-4">
                                                <ul className="grid lg:grid-cols-2">
                                                    <li className={passwordConditions.length ? 'text-green-500' : 'text-red-500'}>
                                                        {passwordConditions.length ? '✔' : '✖'} {t('passwordConditionLength')}
                                                    </li>
                                                    <li className={passwordConditions.uppercase ? 'text-green-500' : 'text-red-500'}>
                                                        {passwordConditions.uppercase ? '✔' : '✖'} {t('passwordConditionUppercase')}
                                                    </li>
                                                    <li className={passwordConditions.lowercase ? 'text-green-500' : 'text-red-500'}>
                                                        {passwordConditions.lowercase ? '✔' : '✖'} {t('passwordConditionLowercase')}
                                                    </li>
                                                    <li className={passwordConditions.number ? 'text-green-500' : 'text-red-500'}>
                                                        {passwordConditions.number ? '✔' : '✖'} {t('passwordConditionNumber')}
                                                    </li>
                                                </ul>
                                            </div>
                                        )}

                                        {invalidCredentials && <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center mt-4">{t('invalidCredentials')}</p>}

                                        <ServerStatusNotice />

                                        <button type="submit" className="submitButton w-full mt-7" disabled={loading || serverStatus !== 'ready'}>
                                            {loading || serverStatus === 'checking' ? <LoadindForButton /> : <span className="flex justify-center items-center gap-4">{t('continueButton')} <FaArrowRight /></span>}
                                        </button>

                                        <p className="text-gray-700 mt-4 text-center text-sm bg-white p-4 rounded-lg border border-gray-300">
                                            {t('infoAfterContinue')}{" "}
                                            {t('andByClickingYouAccept')}{" "}
                                            <a
                                                href="/terms"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-700 underline"
                                            >
                                                {t('terms')}
                                            </a>{" "}
                                            {t('and')}{" "}
                                            <a
                                                href="/privacy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-700 underline"
                                            >
                                                {t('privacy')}
                                            </a>.
                                        </p>
                                    </form>
                                    <div className="pt-5 pb-7 w-full text-center ">
                                        <p className="text-gray-700">
                                            {t('alreadyHaveAccount')} <Link to="/login" className="font-semibold underline">{t('login')}</Link>
                                        </p>
                                    </div>
                                </>
                            )}

                            {step === 2 && (
                                <div className="p-4 w-full flex flex-col items-center">
                                    <IoMailOpenOutline className="text-green-500" size={48} />
                                    {/* <h2 className="text-2xl font-bold text-center">{t('checkEmail')}</h2> */}
                                    <p className="text-lg text-gray-800 my-7">
                                        {t('verificationInstruction')}
                                        <span className="font-semibold block">
                                            {formData.email || sessionStorage.getItem('registeredEmail')}
                                        </span>
                                    </p>

                                    <div className="relative w-full">
                                        <label>{t('verificationCode')}</label>
                                        <FaLock className="inputWeddingIcon" />
                                        <input
                                            type="text"
                                            name="verificationCode"
                                            maxLength={10}
                                            value={verificationCode}
                                            onChange={(e) => { setVerificationCode(e.target.value); setErrors({ ...errors, verificationCode: '' }); setInvalidCredentials(false); }}
                                            className={`inputWedding ${errors.verificationCode ? '!border-red-500' : 'border-primary-300'}`}
                                        />
                                    </div>
                                    {errors.verificationCode && <p className="text-red-500 text-sm w-full">{errors.verificationCode}</p>}

                                    {invalidCredentials && <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center mt-4">{t('invalidCredentials')}</p>}

                                    <div className="mt-4 text-sm text-center">
                                        {resendTimer === 0 ? (
                                            <button onClick={handleResendCode} className="text-gray-700 font-semibold underline" disabled={serverStatus !== 'ready'}>{t('resendCode')}</button>
                                        ) : (
                                            <p className="text-gray-500">{t('resendInSeconds', { seconds: resendTimer })}</p>
                                        )}
                                    </div>

                                    <div className="w-full">
                                        <ServerStatusNotice />
                                    </div>

                                    <button type="button" onClick={handleSubmitVerification} className="submitButton w-full mt-7" disabled={loading || serverStatus !== 'ready'}>
                                        {loading || serverStatus === 'checking' ? <LoadindForButton /> : <span className="flex justify-center items-center gap-4">{t('continueButton')} <FaArrowRight /></span>}
                                    </button>

                                    <button type="button" onClick={handleBackToStep1} className="text-gray-600 underline mt-4 hover:text-gray-800">
                                        {t('backToStep1')}
                                    </button>
                                </div>
                            )}

                            {step === 3 && <WeddingForm email={formData.email} password={formData.password} />}
                        </div>

                        <div className="hidden lg:flex w-1/2 flex-col items-center text-center py- space-y-5">
                            <img src="/logo2.png" className="w-12 h-12 lg:w-32 lg:h-32" alt="Weddinfy" />
                            <AuthSideVisual />
                            <p className="text-gray-700 max-w-md">{t('slogan')}</p>
                        </div>
                    </div>
                </main>
                <footer>
                    <Footer />
                </footer>
            </div>
        </>
    );
};

export default Register;
